/**
 * Liaison Manager supplier scoping (per Jillian, Sep 2026).
 *
 * Some plants have more than one Liaison Manager, each responsible for
 * (and billing) a different set of suppliers there. An LM's Billing
 * Tracker, Suppliers page, Parts page and Plant page should only show the
 * suppliers they're responsible for.
 *
 * The rule, per plant on the LM's Team record:
 *
 *   • ONE Liaison Manager at the plant  → they get EVERY supplier there,
 *     current and future, automatically. Nothing to assign.
 *   • TWO OR MORE Liaison Managers      → each gets exactly the suppliers
 *     checked under "Supplier Assignment" on their Team profile for that
 *     plant (the same SupplierAssignmentsJson map Liaisons already use).
 *     Nothing checked means nothing shows, on purpose: it forces the
 *     assignment to happen the day a second manager is added.
 *
 * Who counts as "a Liaison Manager at the plant": a Team record with
 * PortalRole 'LiaisonManager', not Exited, whose plantCodes include it.
 *
 * Directors, Executives and Regional Managers are never supplier-scoped.
 * Liaisons' existing supplier assignments are untouched by this module.
 *
 * Everything here is pure (no fetching) so it can run wherever the team
 * roster and suppliers are already loaded — usePlantScope() for the
 * Suppliers / Parts / Plant pages, and directly inside the Billing Tracker.
 */

/** Sentinel meaning "every supplier at this plant". */
export const ALL_SUPPLIERS = 'ALL';

const up = (v) => String(v || '').trim().toUpperCase();

function memberPlantCodes(member) {
  const codes = Array.isArray(member?.plantCodes) ? member.plantCodes : [];
  return codes.map(up).filter(Boolean);
}

function isLiaisonManagerRecord(member) {
  return (
    (member?.PortalRole || '') === 'LiaisonManager' &&
    (member?.EmploymentStatus || 'Active') !== 'Exited'
  );
}

/**
 * Liaison Managers (active) whose Team record lists this plant.
 * @returns {Array<object>}
 */
export function liaisonManagersAtPlant(team, plantCode) {
  const target = up(plantCode);
  if (!target) return [];
  return (team || []).filter(
    (m) => isLiaisonManagerRecord(m) && memberPlantCodes(m).includes(target),
  );
}

/**
 * Resolve an LM's supplier scope.
 *
 * @param {object} member  the LM's Team record (needs PortalRole, plantCodes,
 *                         supplierAssignments)
 * @param {Array}  team    full roster from listTeam()
 * @returns {Map<string, 'ALL'|Set<string>>|null}
 *   Map keyed by UPPERCASE plant code → ALL_SUPPLIERS or a Set of UPPERCASE
 *   supplier codes. `null` when the member isn't a Liaison Manager (meaning
 *   "not scoped by supplier at all").
 */
export function resolveSupplierScope(member, team) {
  if (!member || !isLiaisonManagerRecord(member)) return null;
  const scope = new Map();
  const assignments =
    member.supplierAssignments && typeof member.supplierAssignments === 'object'
      ? member.supplierAssignments
      : {};
  for (const plantCode of memberPlantCodes(member)) {
    const managersHere = liaisonManagersAtPlant(team, plantCode);
    // The member themselves may not be in `team` (e.g. a synthesized
    // record); count them in either way so a lone LM is still "one".
    const others = managersHere.filter(
      (m) => String(m.id || '') !== String(member.id || ''),
    );
    if (others.length === 0) {
      scope.set(plantCode, ALL_SUPPLIERS);
      continue;
    }
    // Assignments may be keyed in any case; normalise.
    const raw =
      assignments[plantCode] ||
      assignments[plantCode.toLowerCase()] ||
      Object.entries(assignments).find(([k]) => up(k) === plantCode)?.[1] ||
      [];
    scope.set(
      plantCode,
      new Set((Array.isArray(raw) ? raw : []).map(up).filter(Boolean)),
    );
  }
  return scope;
}

/**
 * Is this supplier in scope at this plant?
 * A `null` scope (unscoped role) allows everything.
 */
export function scopeAllowsSupplierAtPlant(scope, plantCode, supplierCode) {
  if (scope === null || scope === undefined) return true;
  const entry = scope.get(up(plantCode));
  if (!entry) return false; // not one of the LM's plants
  if (entry === ALL_SUPPLIERS) return true;
  return entry.has(up(supplierCode));
}

/**
 * The set of supplier codes an LM may see anywhere: every supplier with a
 * plant assignment at one of their "ALL" plants, plus every explicitly
 * checked code. Needs the suppliers list to know who is at an "ALL" plant.
 *
 * @returns {Set<string>|null}  null when unscoped.
 */
export function scopedSupplierCodes(scope, suppliers) {
  if (scope === null || scope === undefined) return null;
  const out = new Set();
  for (const [plantCode, entry] of scope) {
    if (entry === ALL_SUPPLIERS) {
      for (const s of suppliers || []) {
        const code = up(s.SupplierCode);
        if (!code) continue;
        const here = (s.plantAssignments || []).some(
          (pa) => up(pa.plantCode) === plantCode,
        );
        if (here) out.add(code);
      }
    } else {
      for (const code of entry) out.add(code);
    }
  }
  return out;
}

/**
 * Plants on this LM's record that have two or more Liaison Managers and
 * where this LM has NOTHING checked — their tracker will come up empty
 * there until a Director assigns suppliers. Used for the profile warning.
 *
 * @returns {string[]} UPPERCASE plant codes
 */
export function plantsNeedingSupplierAssignment(member, team) {
  const scope = resolveSupplierScope(member, team);
  if (!scope) return [];
  const out = [];
  for (const [plantCode, entry] of scope) {
    if (entry !== ALL_SUPPLIERS && entry.size === 0) out.push(plantCode);
  }
  return out;
}

/**
 * Plants on this LM's record shared with at least one other Liaison
 * Manager (so the Supplier Assignment checklist actually matters there).
 *
 * @returns {Set<string>} UPPERCASE plant codes
 */
export function sharedManagerPlants(member, team) {
  const out = new Set();
  for (const plantCode of memberPlantCodes(member)) {
    const others = liaisonManagersAtPlant(team, plantCode).filter(
      (m) => String(m.id || '') !== String(member?.id || ''),
    );
    if (others.length > 0) out.add(plantCode);
  }
  return out;
}
